import { Button, Dropdown, Input, InputNumber, Loader, Modal, Message, toaster } from 'rsuite'
import './_host-settings.scss'
import TrashIcon from '@rsuite/icons/Trash'
import PlusIcon from '@rsuite/icons/Plus'
import FileUploadIcon from '@rsuite/icons/FileUpload'
import GlobalIcon from '@rsuite/icons/Global'
import ArrowDownIcon from '@rsuite/icons/ArrowDown'
import { useEffect, useState, useCallback, useRef } from 'react'
import { DomainTarget, getDomainTargetByIp, IDomain } from '@bprotsyk/aso-core'
import { v4 as uuidv4 } from 'uuid'
import { PanelService } from 'network/panel/network'
import { GENERAL_ORIGIN_IP, KEITARO_ORIGIN_IP, STATIC_HOSTING_IP } from '../../../config/runtime'

type DNSRowType = 'A' | 'AAAA' | 'TXT' | 'MX'
type DomainMode = 'general' | 'hosting' | 'keitaro'
type DomainTxtRecord = string | { host?: string | null; value?: string | null }

interface DNSRow {
  id: string
  type: DNSRowType
  host: string
  value: string
  priority: number
  proxied: boolean
  deleted: boolean
}

interface IProps {
  editingDomainData: IDomain | null
  onEditingModal: () => void
  onStaticModal: () => void
  load: boolean
  onDomainDataUpdate: (domain: Partial<IDomain>) => void
}

const TYPE_COLORS: Record<DNSRowType, string> = {
  A: '#2563eb',
  AAAA: '#7c3aed',
  TXT: '#16a34a',
  MX: '#d97706',
}

const ALL_DNS_TYPES: { label: string; value: DNSRowType }[] = [
  { label: 'A', value: 'A' },
  { label: 'AAAA', value: 'AAAA' },
  { label: 'TXT', value: 'TXT' },
  { label: 'MX', value: 'MX' },
]

const HOSTING_DNS_TYPES: { label: string; value: DNSRowType }[] = [
  { label: 'TXT', value: 'TXT' },
  { label: 'MX', value: 'MX' },
]

const TARGET_PRESETS = [
  { label: 'PDL', ip: GENERAL_ORIGIN_IP },
]

const TYPE_PLACEHOLDERS: Record<DNSRowType, string> = {
  A: '0.0.0.0',
  AAAA: '2001:db8::1',
  TXT: 'v=spf1 include:example.com ~all',
  MX: 'mail.example.com',
}

function getValueTextareaRows(value: string): number {
  const wrappedLineCount = value
    .split('\n')
    .reduce((count, line) => count + Math.max(1, Math.ceil(line.length / 52)), 0)
  return Math.min(8, Math.max(2, wrappedLineCount))
}

const PROXIABLE_TYPES = new Set<DNSRowType>(['A', 'AAAA'])

const HOSTING_IP = STATIC_HOSTING_IP
const KEITARO_IP = KEITARO_ORIGIN_IP

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/
const IPV6_RE = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/
const HOST_RE = /^(@|[a-zA-Z0-9]([a-zA-Z0-9\-._]*[a-zA-Z0-9])?)$/

function validateRow(row: DNSRow): string | null {
  if (!HOST_RE.test(row.host)) return `Невалідний хост: "${row.host}"`
  if (row.type === 'A') {
    if (!IPV4_RE.test(row.value)) return `Невалідна IPv4 адреса: "${row.value}"`
    const parts = row.value.split('.').map(Number)
    if (parts.some(p => p > 255)) return `Невалідна IPv4 адреса: "${row.value}"`
  } else if (row.type === 'AAAA') {
    if (!IPV6_RE.test(row.value)) return `Невалідна IPv6 адреса: "${row.value}"`
  } else {
    if (!row.value.trim()) return `Значення не може бути порожнім для ${row.type} запису`
  }
  return null
}

function getModeFromTarget(target: string | undefined): DomainMode {
  if (target === DomainTarget.HOSTING) return 'hosting'
  if (target === DomainTarget.KEITARO) return 'keitaro'
  return 'general'
}

function displayHost(host: string | undefined): string {
  if (!host || host === '@') return ''
  return host
}

function getTxtRecordHost(record: DomainTxtRecord): string {
  return typeof record === 'string' ? '' : displayHost(record.host || undefined)
}

function getTxtRecordValue(record: DomainTxtRecord): string {
  return typeof record === 'string'
    ? record
    : String(record?.value || '')
}

function buildRows(data: IDomain | null): DNSRow[] {
  if (!data) return []
  const rows: DNSRow[] = []

  const aRecords = (data as any).aRecords || []
  if (aRecords.length > 0) {
    aRecords.forEach((rec: { host: string, value: string, proxied: boolean }) => {
      if (rec.value) rows.push({ id: uuidv4(), type: 'A', host: displayHost(rec.host), value: rec.value, priority: 0, proxied: rec.proxied || false, deleted: false })
    })
  } else if (data.ip && data.ip !== 'none' && data.ip !== '') {
    rows.push({ id: uuidv4(), type: 'A', host: '', value: data.ip, priority: 0, proxied: data.proxied || false, deleted: false })
  }

  ;((data as any).aaaaRecords || []).forEach((rec: { host: string, value: string }) => {
    if (rec.value) rows.push({ id: uuidv4(), type: 'AAAA', host: displayHost(rec.host), value: rec.value, priority: 0, proxied: false, deleted: false })
  })

  ;((data.txtRecords || []) as DomainTxtRecord[]).forEach((txt) => {
    const value = getTxtRecordValue(txt)
    if (value) {
      rows.push({
        id: uuidv4(),
        type: 'TXT',
        host: getTxtRecordHost(txt),
        value,
        priority: 0,
        proxied: false,
        deleted: false
      })
    }
  })

  const mxRecords = Array.isArray((data as any).mxRecords) && (data as any).mxRecords.length > 0
    ? (data as any).mxRecords
    : (data.mxRecord && data.mxRecord !== 'none' && data.mxRecord !== ''
      ? [{ host: '@', value: data.mxRecord, priority: data.mxPriority || 10 }]
      : [])

  mxRecords.forEach((rec: { host: string, value: string, priority: number }) => {
    if (rec.value) {
      rows.push({
        id: uuidv4(),
        type: 'MX',
        host: displayHost(rec.host),
        value: rec.value,
        priority: rec.priority || 10,
        proxied: false,
        deleted: false
      })
    }
  })

  return rows
}

function PresetDropdown({ onSelect }: { onSelect: (ip: string) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className="dns-preset-wrap" ref={ref}>
      <button
        type="button"
        className="dns-preset-arrow"
        onClick={() => setOpen(!open)}
        title="Обрати з шаблонів"
      >
        <ArrowDownIcon />
      </button>
      {open && (
        <div className="dns-preset-dropdown">
          {TARGET_PRESETS.map((p) => (
            <button
              key={p.ip}
              type="button"
              className="dns-preset-option"
              onClick={() => { onSelect(p.ip); setOpen(false) }}
            >
              <strong>{p.label}</strong>
              <code>{p.ip}</code>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function EditingRecordsModal({
  editingDomainData,
  onEditingModal,
  onStaticModal,
  load,
  onDomainDataUpdate,
}: IProps) {
  const [mode, setMode] = useState<DomainMode>(() => getModeFromTarget(editingDomainData?.target))
  const [rows, setRows] = useState<DNSRow[]>([])
  const [dynadotNameServers, setDynadotNameServers] = useState<string[]>(['', ''])
  const [note, setNote] = useState<string>(editingDomainData?.note || '')
  const [isChanged, setIsChanged] = useState(false)
  const [saving, setSaving] = useState(false)
  const isDynadotHosting = editingDomainData?.registrar === 'dynadot' && mode === 'hosting'

  useEffect(() => {
    setMode(getModeFromTarget(editingDomainData?.target))
    setRows(buildRows(editingDomainData))
    setDynadotNameServers([
      editingDomainData?.nameServers?.[0] || '',
      editingDomainData?.nameServers?.[1] || '',
    ])
    setNote(editingDomainData?.note || '')
    setIsChanged(false)
  }, [editingDomainData])

  const handleModeChange = (newMode: DomainMode) => {
    setMode(newMode)
    setIsChanged(true)
  }

  const updateRow = useCallback((id: string, field: keyof DNSRow, value: any) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
    setIsChanged(true)
  }, [])

  const addRow = (type: DNSRowType) => {
    setRows((prev) => [
      ...prev,
      { id: uuidv4(), type, host: '', value: '', priority: type === 'MX' ? 10 : 0, proxied: false, deleted: false },
    ])
    setIsChanged(true)
  }

  const confirmDelete = (id: string) => {
    const row = rows.find((r) => r.id === id)
    if (row && PROXIABLE_TYPES.has(row.type)) {
      const activeAddressRows = rows.filter((r) => !r.deleted && PROXIABLE_TYPES.has(r.type))
      if (activeAddressRows.length <= 1) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Не можна видалити останній A/AAAA запис. Додайте інший перед видаленням.
          </Message>,
          { placement: 'topEnd', duration: 4000 }
        )
        return
      }
    }
    if (!window.confirm('Видалити цей DNS-запис?')) return
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, deleted: true } : r)))
    setIsChanged(true)
  }

  const handleCancel = () => {
    setMode(getModeFromTarget(editingDomainData?.target))
    setRows(buildRows(editingDomainData))
    setDynadotNameServers([
      editingDomainData?.nameServers?.[0] || '',
      editingDomainData?.nameServers?.[1] || '',
    ])
    setNote(editingDomainData?.note || '')
    setIsChanged(false)
    onEditingModal()
  }

  const handleSave = async () => {
    if (!isChanged || saving) return

    if (isDynadotHosting) {
      const normalizedNameServers = dynadotNameServers
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)

      if (normalizedNameServers.length !== 0 && normalizedNameServers.length !== 2) {
        toaster.push(
          <Message showIcon type="error" closable>
            Для Dynadot потрібно вказати 2 nameserver-и
          </Message>,
          { placement: 'topEnd', duration: 4000 }
        )
        return
      }

      setSaving(true)
      try {
        const body: any = {
          ...editingDomainData,
          registrar: 'dynadot',
          target: DomainTarget.HOSTING,
          ip: HOSTING_IP,
          proxied: false,
          nameServers: normalizedNameServers,
          note,
        }

        const response = await PanelService.editDomain(body)
        onDomainDataUpdate(response?.data || body)
        onEditingModal()
      } catch (err: any) {
        const serverMsg = err?.response?.data?.error || err?.response?.data || err?.message || 'Невідома помилка'
        toaster.push(
          <Message showIcon type="error" closable>
            {typeof serverMsg === 'string' ? serverMsg : 'Помилка збереження'}
          </Message>,
          { placement: 'topEnd', duration: 5000 }
        )
      } finally {
        setSaving(false)
      }
      return
    }

    const active = rows.filter((r) => !r.deleted).map((r) => ({
      ...r,
      host: r.host.trim() || '@',
    }))

    for (const row of active) {
      const err = validateRow(row)
      if (err) {
        toaster.push(
          <Message showIcon type="error" closable>{err}</Message>,
          { placement: 'topEnd', duration: 4000 }
        )
        return
      }
    }

    const seen = new Set<string>()
    for (const row of active) {
      const key = row.type === 'MX'
        ? `${row.type}:${row.host}:${row.value}:${row.priority || 10}`
        : `${row.type}:${row.host}:${row.value}`
      if (seen.has(key)) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Дублікат: {row.type} {row.host} — видаліть повтор
          </Message>,
          { placement: 'topEnd', duration: 4000 }
        )
        return
      }
      seen.add(key)
    }

    setSaving(true)
    const aRows = active.filter((r) => r.type === 'A')
    const aaaaRows = active.filter((r) => r.type === 'AAAA')
    const mxRows = active.filter((r) => r.type === 'MX')
    const txtRows = active.filter((r) => r.type === 'TXT')

    const rootA = aRows.find((r) => (r.host || '@') === '@' || r.host === '')
    let finalIp = rootA?.value || ''
    let finalTarget: string = getDomainTargetByIp(finalIp) || 'unknown'
    const finalProxied = rootA?.proxied || aaaaRows[0]?.proxied || false

    if (mode === 'hosting') {
      finalIp = HOSTING_IP
      finalTarget = DomainTarget.HOSTING
    } else if (mode === 'keitaro') {
      finalIp = KEITARO_IP
      finalTarget = DomainTarget.KEITARO
    }

    const body: any = {
      ...editingDomainData,
      ip: finalIp,
      aRecords: aRows.map((r) => ({ host: r.host || '@', value: r.value, proxied: r.proxied || false })),
      target: finalTarget,
      txtRecords: txtRows
        .map((r) => ({ host: r.host || '@', value: r.value }))
        .filter((r) => r.value),
      mxRecords: mxRows.map((r) => ({ host: r.host || '@', value: r.value, priority: r.priority || 10 })).filter((r) => r.value),
      mxRecord: mxRows[0]?.value || '',
      mxPriority: mxRows[0]?.priority || 10,
      proxied: finalProxied,
      note,
      aaaaRecords: aaaaRows.map((r) => ({ host: r.host || '@', value: r.value })),
    }

    try {
      await PanelService.editDomain(body)
      onDomainDataUpdate(body)
      onEditingModal()
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error || err?.response?.data || err?.message || 'Невідома помилка'
      toaster.push(
        <Message showIcon type="error" closable>
          {typeof serverMsg === 'string' ? serverMsg : 'Помилка збереження'}
        </Message>,
        { placement: 'topEnd', duration: 5000 }
      )
    } finally {
      setSaving(false)
    }
  }

  const handleRenew = async () => {
    if (!editingDomainData) return
    const days = editingDomainData.expiresAt
      ? Math.floor((editingDomainData.expiresAt - Date.now()) / 86400000)
      : null
    if (days === null || days > 14) return

    const ok = window.confirm(`Поновити "${editingDomainData.name}" на 1 рік?`)
    if (!ok) return

    try {
      const result = await PanelService.renewDomain({ domainName: editingDomainData.name || '', years: 1 })
      if (result.success) {
        toaster.push(
          <Message showIcon type="success" closable>
            Поновлено до {new Date(result.data.expiredDate).toLocaleDateString('uk-UA')}
          </Message>,
          { placement: 'topEnd', duration: 5000 }
        )
        onDomainDataUpdate({ ...editingDomainData, expiresAt: new Date(result.data.expiredDate).getTime() })
        onEditingModal()
      } else {
        toaster.push(<Message showIcon type="error" closable>{result.message}</Message>, { placement: 'topEnd', duration: 5000 })
      }
    } catch (e: any) {
      toaster.push(<Message showIcon type="error" closable>{e.message}</Message>, { placement: 'topEnd', duration: 5000 })
    }
  }

  const activeRows = rows.filter((r) => !r.deleted)
  const daysUntilExpiry = editingDomainData?.expiresAt
    ? Math.floor((editingDomainData.expiresAt - Date.now()) / 86400000)
    : null
  const canRenew = daysUntilExpiry !== null && daysUntilExpiry <= 14

  const dynadotNsCount = dynadotNameServers.filter(Boolean).length
  const dynadotArchiveReady = Boolean(editingDomainData?.hostingArchiveUploadedAt)
  const dynadotStatusLabel = editingDomainData?.functionalStatus === 'healthy'
    ? 'Готово'
    : dynadotArchiveReady && dynadotNsCount === 2
      ? 'Очікуємо NS / SSL'
      : 'Потрібно завершити налаштування'

  const dnsTypes = mode === 'hosting' ? HOSTING_DNS_TYPES : ALL_DNS_TYPES

  const renderDnsTable = (rowsToShow: DNSRow[]) => {
    const isAddressType = (t: DNSRowType) => t === 'A' || t === 'AAAA'
    return (
      <table className="dns-table">
        <thead>
          <tr>
            <th className="dns-col-type">Тип</th>
            <th className="dns-col-name">Хост</th>
            <th className="dns-col-value">Значення</th>
            <th className="dns-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {rowsToShow.map((row) => (
            <tr key={row.id} className="dns-row">
              <td className="dns-col-type">
                <span className="dns-type-badge" style={{ background: TYPE_COLORS[row.type] }}>
                  {row.type}
                </span>
              </td>
              <td className="dns-col-name">
                <Input
                  size="sm"
                  value={row.host}
                  onChange={(v) => updateRow(row.id, 'host', v)}
                  placeholder="@"
                  className="dns-input"
                />
              </td>
              <td className="dns-col-value">
                <div className="dns-value-cell">
                  {isAddressType(row.type) ? (
                    <Input
                      size="sm"
                      value={row.value}
                      onChange={(v) => updateRow(row.id, 'value', v)}
                      placeholder={TYPE_PLACEHOLDERS[row.type]}
                      className="dns-input dns-value-input"
                    />
                  ) : row.type === 'TXT' ? (
                    <Input
                      as="textarea"
                      rows={getValueTextareaRows(row.value)}
                      value={row.value}
                      onChange={(v) => updateRow(row.id, 'value', v)}
                      placeholder={TYPE_PLACEHOLDERS[row.type]}
                      className="dns-input dns-value-input dns-value-textarea"
                    />
                  ) : (
                    <Input
                      size="sm"
                      value={row.value}
                      onChange={(v) => updateRow(row.id, 'value', v)}
                      placeholder={TYPE_PLACEHOLDERS[row.type]}
                      className="dns-input dns-value-input"
                    />
                  )}
                  {isAddressType(row.type) && (
                    <PresetDropdown onSelect={(ip) => updateRow(row.id, 'value', ip)} />
                  )}
                  {row.type === 'MX' && (
                    <InputNumber
                      size="sm"
                      className="dns-priority-input"
                      value={row.priority}
                      onChange={(v) => updateRow(row.id, 'priority', Number(v))}
                      min={1}
                      max={100}
                    />
                  )}
                </div>
              </td>
              <td className="dns-col-actions">
                <div className="dns-row-actions">
                  {PROXIABLE_TYPES.has(row.type) && (
                    <button
                      type="button"
                      className={`dns-proxy-btn${row.proxied ? ' active' : ''}`}
                      onClick={() => { updateRow(row.id, 'proxied', !row.proxied) }}
                      title={row.proxied ? 'Cloudflare проксі: УВІМК' : 'Cloudflare проксі: ВИМК'}
                    >
                      <img src="/icons/cloudflare.png" className="dns-proxy-icon" alt="CF" />
                    </button>
                  )}
                  <Button
                    size="sm"
                    appearance="subtle"
                    className="dns-del-btn"
                    onClick={() => confirmDelete(row.id)}
                  >
                    <TrashIcon />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }
  
  return (
    <>
      <Modal
        backdrop="static"
        size="lg"
        open
        className={`dns-modal${isChanged ? ' dns-modal-dirty' : ''}`}
        onClose={() => !isChanged && onEditingModal()}
      >
        <Modal.Header>
          <div className="dns-modal-header-inner">
            <a
              href={`https://${editingDomainData?.name}`}
              target="_blank"
              rel="noreferrer"
              className="dns-domain-name"
            >
              {editingDomainData?.name}
            </a>
            {editingDomainData?.registrar === 'dynadot' ? (
              <div className="dns-mode-tabs dns-mode-tabs-single">
                <button className="dns-mode-tab active" type="button">
                  Dynadot Hosting
                </button>
              </div>
            ) : (
              <div className="dns-mode-tabs">
                <button
                  className={`dns-mode-tab${mode === 'general' ? ' active' : ''}`}
                  onClick={() => handleModeChange('general')}
                >
                  DNS
                </button>
                <button className={`dns-mode-tab${mode === 'hosting' ? ' active' : ''}`} onClick={() => handleModeChange('hosting')} disabled={!HOSTING_IP}>
                  Хостинг
                </button>
                <button
                  className={`dns-mode-tab${mode === 'keitaro' ? ' active' : ''}`}
                  onClick={() => handleModeChange('keitaro')}
                  disabled={!KEITARO_IP}
                >
                  Keitaro
                </button>
              </div>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className="dns-modal-body">
          {mode === 'general' && (
            activeRows.length === 0
              ? <div className="dns-empty">Записів немає. Натисніть "Додати запис" нижче.</div>
              : renderDnsTable(activeRows)
          )}

          {mode === 'hosting' && (
            <>
              <div className={`dns-hosting-card${editingDomainData?.registrar === 'dynadot' ? ' dns-hosting-card-dynadot' : ''}`}>
                <div className="dns-hosting-info-row">
                  <div className="dns-hosting-meta">
                    <span className="dns-hosting-badge">{editingDomainData?.registrar === 'dynadot' ? 'Dynadot Hosting' : 'Статичний хостинг'}</span>
                    <span className="dns-hosting-ip">{HOSTING_IP}</span>
                    {editingDomainData?.registrar === 'dynadot' && (
                      <span className={`dns-hosting-status-badge${editingDomainData?.functionalStatus === 'healthy' ? ' is-ready' : ''}`}>
                        {dynadotStatusLabel}
                      </span>
                    )}
                  </div>
                  <a
                    href={`https://${editingDomainData?.name}`}
                    target="_blank"
                    rel="noreferrer"
                    className="dns-hosting-preview-link"
                  >
                    <GlobalIcon /> Переглянути
                  </a>
                </div>
                {editingDomainData?.registrar === 'dynadot' ? (
                  <div className="dns-dynadot-shell">
                    <div className="dns-dynadot-intro">
                      <p className="dns-dynadot-lead">Налаштуйте 2 nameserver-и, завантажте архів і панель тихо доведе домен до готовності.</p>
                      <p className="dns-dynadot-caption">
                        SSL і повторні спроби деплою хостингу виконуються у фоні після того, як NS та архів уже задані.
                      </p>
                    </div>

                    <div className="dns-dynadot-grid">
                      <div className="dns-dynadot-field">
                        <label className="dns-note-label">Nameserver 1</label>
                        <Input
                          value={dynadotNameServers[0]}
                          onChange={(value) => {
                            setDynadotNameServers((prev) => [value, prev[1] || ''])
                            setIsChanged(true)
                          }}
                          placeholder="ns1.example.com"
                          className="dns-note-input"
                        />
                      </div>
                      <div className="dns-dynadot-field">
                        <label className="dns-note-label">Nameserver 2</label>
                        <Input
                          value={dynadotNameServers[1]}
                          onChange={(value) => {
                            setDynadotNameServers((prev) => [prev[0] || '', value])
                            setIsChanged(true)
                          }}
                          placeholder="ns2.example.com"
                          className="dns-note-input"
                        />
                      </div>
                    </div>

                    <div className="dns-dynadot-status-grid">
                      <div className="dns-dynadot-status-item">
                        <span className="dns-dynadot-status-label">Nameserver-и</span>
                        <strong>{dynadotNsCount === 2 ? 'готово' : 'потрібно 2'}</strong>
                      </div>
                      <div className="dns-dynadot-status-item">
                        <span className="dns-dynadot-status-label">Архів</span>
                        <strong>{dynadotArchiveReady ? 'завантажено' : 'ще не завантажено'}</strong>
                      </div>
                      <div className="dns-dynadot-status-item">
                        <span className="dns-dynadot-status-label">Стан</span>
                        <strong>{dynadotStatusLabel}</strong>
                      </div>
                    </div>

                    {editingDomainData?.provisioningError ? (
                      <div className="dns-dynadot-warning">
                        Остання причина очікування: {editingDomainData.provisioningError}
                      </div>
                    ) : null}

                    <button className="dns-upload-card-btn" onClick={onStaticModal} disabled={load}>
                      {load ? <Loader size="sm" /> : <><FileUploadIcon /> {editingDomainData?.hostingArchiveUploadedAt ? 'Перезавантажити архів' : 'Завантажити архів'}</>}
                    </button>
                  </div>
                ) : (
                  <button className="dns-upload-card-btn" onClick={onStaticModal} disabled={load}>
                    {load ? <Loader size="sm" /> : <><FileUploadIcon /> {editingDomainData?.hostingArchiveUploadedAt ? 'Перезавантажити архів' : 'Завантажити архів'}</>}
                  </button>
                )}
              </div>

              {editingDomainData?.registrar !== 'dynadot' && activeRows.filter(r => r.type !== 'A' && r.type !== 'AAAA').length > 0 && (
                <>
                  <div className="dns-hosting-records-label">DNS-записи (TXT / MX)</div>
                  {renderDnsTable(activeRows.filter(r => r.type !== 'A' && r.type !== 'AAAA'))}
                </>
              )}
            </>
          )}

          {mode === 'keitaro' && (
            <div className="dns-keitaro-placeholder">
              <div className="dns-keitaro-title">Трекінговий домен</div>
              <div className="dns-keitaro-desc">
                Домен перенаправлено на Keitaro трекер.
                <br />
                Повна інтеграція з Keitaro буде доступна незабаром.
              </div>
              <div className="dns-keitaro-ip">Ціль: {KEITARO_IP}</div>
            </div>
          )}
          <div className={`dns-note-section${isDynadotHosting ? ' dns-note-section-dynadot' : ''}`}>
            <label className="dns-note-label">Примітки</label>
            <Input
              as="textarea"
              rows={1}
              value={note}
              onChange={(v) => { setNote(v); setIsChanged(true) }}
              placeholder="Додати примітку..."
              className="dns-note-input"
            />
          </div>
        </Modal.Body>

        <Modal.Footer className="dns-modal-footer">
          <div className="dns-footer-left">
            {mode !== 'keitaro' && !isDynadotHosting && (
              <Dropdown
                size="sm"
                appearance="subtle"
                title={<span className="dns-add-label"><PlusIcon /> Додати запис</span> as any}
                className="dns-add-dropdown"
              >
                {dnsTypes.map((t) => (
                  <Dropdown.Item key={t.value} onSelect={() => addRow(t.value)} className="dns-type-item">
                    <span className="dns-type-badge dns-type-badge-sm" style={{ background: TYPE_COLORS[t.value] }}>
                      {t.value}
                    </span>
                    {t.label}
                </Dropdown.Item>
              ))}
            </Dropdown>
            )}
          </div>
          <div className="dns-footer-right">
            {canRenew && (
              <Button size="sm" appearance="link" className="dns-renew-btn" onClick={handleRenew}>
                Поновити ({daysUntilExpiry}д)
              </Button>
            )}
            <Button appearance="subtle" onClick={handleCancel}>
              Скасувати
          </Button>
            <Button appearance="primary" disabled={!isChanged || saving} onClick={handleSave}>
              {saving ? <Loader size="xs" /> : 'Зберегти'}
          </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}
